import React, { useEffect, useState, useRef } from "react";
import DOMPurify from "dompurify";
import { db, functions } from "../firebase";
import { collection, addDoc, query, where, onSnapshot, deleteDoc, doc, updateDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import ZeichenCanvas from "../components/ZeichenCanvas";

const openaiChat = httpsCallable(functions, "openaiChat");

// Bild komprimieren → Base64 JPEG (max 900px breit, Qualität 0.65)
function bildKomprimieren(datei) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(datei);
    img.onload = () => {
      const maxBreite = 900;
      const scale = img.width > maxBreite ? maxBreite / img.width : 1;
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL("image/jpeg", 0.65));
    };
    img.onerror = reject;
    img.src = url;
  });
}

// ─── Rich-Text-Editor ─────────────────────────────────────────────────────────
function RichTextEditor({ initialWert, onChange }) {
  const editorRef = useRef(null);
  const initialisiert = useRef(false);

  useEffect(() => {
    if (!initialisiert.current && editorRef.current && initialWert !== undefined) {
      editorRef.current.innerHTML = DOMPurify.sanitize(initialWert || "");
      initialisiert.current = true;
    }
  }, [initialWert]);

  const format = (cmd, wert) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, wert || null);
  };

  const TOOLBAR = [
    { label: <b>F</b>,  title: "Fett",         cmd: "bold" },
    { label: <i>K</i>,  title: "Kursiv",        cmd: "italic" },
    { label: <u>U</u>,  title: "Unterstrichen", cmd: "underline" },
    { label: "•",       title: "Liste",         cmd: "insertUnorderedList" },
    { label: "1.",      title: "Nummerierte Liste", cmd: "insertOrderedList" },
    { label: "H",       title: "Überschrift",   cmd: "formatBlock", wert: "H3" },
    { label: "¶",       title: "Normal",        cmd: "formatBlock", wert: "P" },
    { label: "—",       title: "Trennlinie",    cmd: "insertHorizontalRule" },
  ];

  return (
    <div style={rtStyles.wrapper}>
      <div style={rtStyles.toolbar}>
        {TOOLBAR.map((btn, i) => (
          <button
            key={i}
            title={btn.title}
            style={rtStyles.toolBtn}
            onMouseDown={(e) => { e.preventDefault(); format(btn.cmd, btn.wert); }}
          >
            {btn.label}
          </button>
        ))}
      </div>
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={(e) => onChange(e.currentTarget.innerHTML)}
        style={rtStyles.editor}
      />
    </div>
  );
}

const rtStyles = {
  wrapper: { display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" },
  toolbar: {
    display: "flex", flexWrap: "wrap", gap: "2px", padding: "4px 6px",
    backgroundColor: "#f0f2f5", borderBottom: "1px solid #ddd", flexShrink: 0,
  },
  toolBtn: {
    minWidth: "28px", height: "26px", padding: "0 5px",
    border: "1px solid #ccc", borderRadius: "3px", backgroundColor: "white",
    cursor: "pointer", fontSize: "0.85rem", fontFamily: "sans-serif",
    display: "flex", alignItems: "center", justifyContent: "center",
  },
  editor: {
    flex: 1, padding: "0.8rem", fontSize: "0.95rem", overflowY: "auto",
    outline: "none", lineHeight: 1.5, fontFamily: "sans-serif",
  },
};

// ─── Meeting-Liste ────────────────────────────────────────────────────────────
export default function Meeting({ user }) {
  const [meetings, setMeetings] = useState([]);
  const [aktivesMeeting, setAktivesMeeting] = useState(null);
  const [neuerTitel, setNeuerTitel] = useState("");
  const [neuesDatum, setNeuesDatum] = useState(new Date().toISOString().slice(0, 10));

  useEffect(() => {
    const q = query(collection(db, "meetings"), where("uid", "==", user.uid));
    return onSnapshot(q, (snap) => {
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      data.sort((a, b) => (b.erstellt?.toMillis?.() || 0) - (a.erstellt?.toMillis?.() || 0));
      setMeetings(data);
      if (aktivesMeeting) {
        const aktuell = data.find((m) => m.id === aktivesMeeting.id);
        if (aktuell) setAktivesMeeting(aktuell);
      }
    });
  }, [user.uid]);

  const meetingErstellen = async (e) => {
    e.preventDefault();
    if (!neuerTitel.trim()) return;
    const ref2 = await addDoc(collection(db, "meetings"), {
      titel: neuerTitel, datum: neuesDatum,
      aufzeichnung: "", notizen: "", meetingNotizen: "", zeichnung: null, screenshots: [],
      uid: user.uid, erstellt: new Date()
    });
    setNeuerTitel("");
    setAktivesMeeting({ id: ref2.id, titel: neuerTitel, datum: neuesDatum, aufzeichnung: "", notizen: "", meetingNotizen: "", zeichnung: null, screenshots: [] });
  };

  if (aktivesMeeting) {
    return (
      <MeetingDetail
        meeting={aktivesMeeting}
        onZurueck={() => setAktivesMeeting(null)}
        user={user}
      />
    );
  }

  return (
    <div style={styles.seite}>
      <h2 style={styles.titel}>Meetings</h2>

      <form onSubmit={meetingErstellen} style={styles.form}>
        <input style={styles.input} value={neuerTitel} onChange={(e) => setNeuerTitel(e.target.value)} placeholder="Meeting-Titel..." />
        <input style={styles.inputDatum} type="date" value={neuesDatum} onChange={(e) => setNeuesDatum(e.target.value)} />
        <button style={styles.button} type="submit">+ Erstellen</button>
      </form>

      {meetings.length === 0 && <p style={styles.leer}>Noch keine Meetings.</p>}
      {meetings.map((m) => (
        <div key={m.id} style={styles.meetingKarte} onClick={() => setAktivesMeeting(m)}>
          <div style={styles.meetingInfo}>
            <strong>{m.titel}</strong>
            <span style={styles.datum}>📅 {m.datum}</span>
          </div>
          <div style={styles.meetingMeta}>
            {m.aufzeichnung && <span style={styles.badge}>🎙️</span>}
            {m.meetingNotizen && <span style={styles.badge}>📝</span>}
            {m.notizen && <span style={styles.badge}>📋</span>}
            {m.zeichnung && <span style={styles.badge}>✏️</span>}
            {m.screenshots?.length > 0 && <span style={styles.badge}>🖼️ {m.screenshots.length}</span>}
            <button style={styles.loeschBtn} onClick={(e) => { e.stopPropagation(); deleteDoc(doc(db, "meetings", m.id)); }}>🗑️</button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Meeting-Detail mit 5 Panels ──────────────────────────────────────────────
function MeetingDetail({ meeting, onZurueck, user }) {
  const [aufzeichnung, setAufzeichnung] = useState(meeting.aufzeichnung || "");
  const [notizen, setNotizen] = useState(meeting.notizen || "");
  const [meetingNotizen, setMeetingNotizen] = useState(meeting.meetingNotizen || "");
  const [zeichnung, setZeichnung] = useState(meeting.zeichnung || null);
  const [screenshots, setScreenshots] = useState(meeting.screenshots || []);
  const [hoeren, setHoeren] = useState(false);
  const [gespeichert, setGespeichert] = useState(false);
  const [editTitel, setEditTitel] = useState(false);
  const [zusammenfassungLaeuft, setZusammenfassungLaeuft] = useState(false);
  const [titelText, setTitelText] = useState(meeting.titel);

  const canvasDomRef = useRef(null);
  const screenshotRef = useRef();
  const erkennungRef = useRef(null);
  const bildSpeichernRef = useRef(null);

  // Mikrofon
  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    const erk = new SR();
    erk.lang = "de-DE";
    erk.continuous = true;
    erk.interimResults = false;
    erk.onresult = (e) => {
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) {
          const transkript = e.results[i][0].transcript.trim();
          setAufzeichnung((prev) => prev + (prev ? "\n" : "") + transkript);
        }
      }
    };
    erk.onend = () => setHoeren(false);
    erkennungRef.current = erk;
    return () => erk.stop();
  }, []);

  const zusammenfassungErstellen = async () => {
    if (!aufzeichnung.trim()) { alert("Keine Aufzeichnung vorhanden."); return; }
    setZusammenfassungLaeuft(true);
    try {
      const { data } = await openaiChat({
        model: "gpt-4o-mini",
        messages: [
          { role: "system", content: "Du bist ein hilfreicher Assistent. Erstelle eine strukturierte Zusammenfassung des folgenden Meeting-Protokolls auf Deutsch. Gliedere es in: Hauptthemen, Beschlüsse, Offene Punkte." },
          { role: "user", content: aufzeichnung },
        ],
        max_tokens: 1000,
      });
      const zusammenfassung = data.content;
      if (zusammenfassung) {
        setNotizen((prev) => prev ? prev + "\n\n--- GPT Zusammenfassung ---\n" + zusammenfassung : "--- GPT Zusammenfassung ---\n" + zusammenfassung);
      } else {
        alert("Keine Antwort von GPT erhalten.");
      }
    } catch (err) { alert("Fehler: " + err.message); }
    setZusammenfassungLaeuft(false);
  };

  const mikrofonToggle = () => {
    if (!erkennungRef.current) { alert("Mikrofon nicht verfügbar."); return; }
    if (hoeren) { erkennungRef.current.stop(); setHoeren(false); }
    else { erkennungRef.current.start(); setHoeren(true); }
  };

  const speichern = async () => {
    let aktuelleZeichnung = zeichnung;
    if (canvasDomRef.current) {
      const exportiert = canvasDomRef.current.toDataURL("image/png");
      if (exportiert) { aktuelleZeichnung = exportiert; setZeichnung(exportiert); }
    }
    await updateDoc(doc(db, "meetings", meeting.id), {
      aufzeichnung, notizen, meetingNotizen, zeichnung: aktuelleZeichnung, screenshots
    });
    setGespeichert(true);
    setTimeout(() => setGespeichert(false), 2000);
  };

  const titelSpeichern = async () => {
    await updateDoc(doc(db, "meetings", meeting.id), { titel: titelText });
    setEditTitel(false);
  };

  const screenshotHochladen = async (e) => {
    const datei = e.target.files[0];
    if (!datei) return;
    await bildSpeichern(datei);
  };

  const bildSpeichern = async (datei) => {
    try {
      const base64 = await bildKomprimieren(datei);
      const neuerScreenshot = {
        id: Date.now().toString(),
        name: datei.name || "Screenshot",
        data: base64,
        erstellt: Date.now(),
      };
      setScreenshots((prev) => [...prev, neuerScreenshot]);
    } catch (err) { alert("Fehler beim Verarbeiten: " + err.message); }
  };

  bildSpeichernRef.current = bildSpeichern;
  useEffect(() => {
    const handlePaste = (e) => {
      let dateiGefunden = null;
      const items = e.clipboardData?.items;
      if (items) {
        for (const item of Array.from(items)) {
          if (item.kind === "file" && item.type.startsWith("image/")) {
            dateiGefunden = item.getAsFile();
            if (dateiGefunden) break;
          }
        }
      }
      if (!dateiGefunden) {
        const files = e.clipboardData?.files;
        if (files) {
          for (const f of Array.from(files)) {
            if (f.type.startsWith("image/")) { dateiGefunden = f; break; }
          }
        }
      }
      if (dateiGefunden) {
        e.preventDefault();
        setTimeout(() => bildSpeichernRef.current(dateiGefunden), 0);
      }
    };
    document.addEventListener("paste", handlePaste);
    return () => document.removeEventListener("paste", handlePaste);
  }, []);

  const screenshotLoeschen = (id) => setScreenshots(screenshots.filter((s) => s.id !== id));

  return (
    <div style={styles.detailWrapper}>
      {/* Header */}
      <div style={styles.detailHeader}>
        <button style={styles.zurueckBtn} onClick={onZurueck}>← Zurück</button>
        <div style={styles.titelBereich}>
          {editTitel ? (
            <>
              <input style={styles.titelInput} value={titelText} onChange={(e) => setTitelText(e.target.value)} autoFocus />
              <button style={styles.speichernBtn} onClick={titelSpeichern}>💾</button>
              <button style={styles.abbrechenBtn} onClick={() => setEditTitel(false)}>✕</button>
            </>
          ) : (
            <>
              <h2 style={styles.meetingTitel}>{meeting.titel}</h2>
              <span style={styles.meetingDatum}>📅 {meeting.datum}</span>
              <button style={styles.iconBtn} onClick={() => setEditTitel(true)}>✏️</button>
            </>
          )}
        </div>
        <button
          style={{ ...styles.button, backgroundColor: gespeichert ? "#34a853" : "#1a73e8" }}
          onClick={speichern}
        >
          {gespeichert ? "✅ Gespeichert!" : "💾 Speichern"}
        </button>
      </div>

      {/* 5 Panels */}
      <div style={styles.panelGrid}>

        {/* Panel 1: Aufzeichnung */}
        <div style={styles.panel}>
          <div style={styles.panelHeader}>
            <span>🎙️ Aufzeichnung</span>
            <button
              style={{ ...styles.mikrofonBtn, backgroundColor: hoeren ? "#ea4335" : "#f0f2f5", color: hoeren ? "white" : "#333" }}
              onClick={mikrofonToggle}
            >
              {hoeren ? "⏹ Stopp" : "🎙️ Start"}
            </button>
          </div>
          {hoeren && <div style={styles.aufnahmeIndikator}>🔴 Aufnahme läuft...</div>}
          <textarea
            style={styles.panelTextarea}
            value={aufzeichnung}
            onChange={(e) => setAufzeichnung(e.target.value)}
            placeholder="Gesprochenes erscheint hier automatisch. Oder manuell tippen..."
          />
          <button
            style={{ ...styles.gptBtn, opacity: zusammenfassungLaeuft ? 0.7 : 1 }}
            onClick={zusammenfassungErstellen}
            disabled={zusammenfassungLaeuft}
          >
            {zusammenfassungLaeuft ? "⏳ GPT denkt..." : "✨ Zusammenfassung erstellen"}
          </button>
        </div>

        {/* Panel 2: Notizen (Rich-Text) */}
        <div style={styles.panel}>
          <div style={styles.panelHeader}>
            <span>📝 Notizen</span>
          </div>
          <RichTextEditor
            initialWert={meetingNotizen}
            onChange={setMeetingNotizen}
          />
        </div>

        {/* Panel 3: Ablage Zusammenfassung */}
        <div style={styles.panel}>
          <div style={styles.panelHeader}>
            <span>📋 Ablage Zusammenfassung</span>
          </div>
          <textarea
            style={styles.panelTextarea}
            value={notizen}
            onChange={(e) => setNotizen(e.target.value)}
            placeholder="Zusammenfassung und Ablage..."
          />
        </div>

        {/* Panel 4: Zeichnung */}
        <div style={styles.panel}>
          <div style={styles.panelHeader}>
            <span>✏️ Zeichnung</span>
          </div>
          <ZeichenCanvas
            gespeicherteZeichnung={zeichnung}
            canvasRefCallback={(el) => { canvasDomRef.current = el; }}
          />
        </div>

        {/* Panel 5: Screenshots */}
        <div style={styles.panel}>
          <div style={styles.panelHeader}>
            <span>🖼️ Screenshots</span>
            <button style={styles.kleinBtn} onClick={() => screenshotRef.current.click()}>
              📁 Datei
            </button>
            <input ref={screenshotRef} type="file" accept="image/*" style={{ display: "none" }} onChange={screenshotHochladen} />
          </div>
          <div style={styles.pasteZone} tabIndex={0}>
            📋 Bild kopieren, hier klicken und Strg+V druecken
          </div>
          <div style={styles.screenshotListe}>
            {screenshots.length === 0 && <p style={styles.leer}>Noch keine Screenshots.</p>}
            {screenshots.map((s) => (
              <div key={s.id} style={styles.screenshotItem}>
                <img src={s.data || s.url} alt={s.name} style={styles.screenshotBild} onClick={() => window.open(s.data || s.url, "_blank")} />
                <div style={styles.screenshotFooter}>
                  <span style={styles.screenshotName}>{s.name}</span>
                  <button style={{ ...styles.iconBtn, color: "#333" }} onClick={() => screenshotLoeschen(s.id)}>🗑️</button>
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
}

const styles = {
  // Liste
  seite: { padding: "clamp(0.75rem, 2.5vw, 2rem)", width: "100%", boxSizing: "border-box" },
  titel: { marginTop: 0, marginBottom: "1.5rem", color: "#1a3558" },
  form: { display: "flex", gap: "0.5rem", marginBottom: "1.5rem", flexWrap: "wrap" },
  input: { flex: 1, padding: "0.6rem", fontSize: "1rem", border: "1px solid #ccc", borderRadius: "4px", minWidth: "150px" },
  inputDatum: { padding: "0.6rem", fontSize: "0.95rem", border: "1px solid #ccc", borderRadius: "4px" },
  button: { padding: "0.6rem 1rem", backgroundColor: "#1a73e8", color: "white", border: "none", borderRadius: "4px", cursor: "pointer" },
  leer: { color: "#999", textAlign: "center" },
  meetingKarte: { backgroundColor: "white", borderRadius: "8px", padding: "1rem", marginBottom: "0.8rem", boxShadow: "0 1px 3px rgba(0,0,0,0.1)", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" },
  meetingInfo: { display: "flex", flexDirection: "column", gap: "0.2rem" },
  meetingMeta: { display: "flex", alignItems: "center", gap: "0.4rem" },
  datum: { fontSize: "0.85rem", color: "#666" },
  badge: { fontSize: "0.85rem", backgroundColor: "#f0f2f5", padding: "0.2rem 0.4rem", borderRadius: "4px" },
  loeschBtn: { background: "none", border: "none", cursor: "pointer", fontSize: "1rem" },
  // Detail
  detailWrapper: { display: "flex", flexDirection: "column", height: "100vh" },
  detailHeader: { display: "flex", alignItems: "center", gap: "1rem", padding: "0.8rem 1rem", backgroundColor: "#1a3558", color: "white", flexShrink: 0, flexWrap: "wrap" },
  zurueckBtn: { padding: "0.4rem 0.8rem", backgroundColor: "rgba(255,255,255,0.2)", color: "white", border: "none", borderRadius: "4px", cursor: "pointer" },
  titelBereich: { display: "flex", alignItems: "center", gap: "0.5rem", flex: 1 },
  meetingTitel: { margin: 0, fontSize: "1.1rem" },
  meetingDatum: { fontSize: "0.85rem", opacity: 0.8 },
  titelInput: { padding: "0.4rem", fontSize: "1rem", border: "1px solid #ccc", borderRadius: "4px", flex: 1 },
  speichernBtn: { padding: "0.3rem 0.6rem", backgroundColor: "#34a853", color: "white", border: "none", borderRadius: "4px", cursor: "pointer" },
  abbrechenBtn: { padding: "0.3rem 0.6rem", backgroundColor: "#ea4335", color: "white", border: "none", borderRadius: "4px", cursor: "pointer" },
  iconBtn: { background: "none", border: "none", cursor: "pointer", fontSize: "1rem", color: "white" },
  // Panel-Grid
  panelGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", flex: 1, gap: "0", overflow: "auto" },
  panel: { display: "flex", flexDirection: "column", border: "1px solid #ddd", backgroundColor: "white", overflow: "hidden" },
  panelHeader: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0.5rem 0.8rem", backgroundColor: "#f8f9fa", borderBottom: "1px solid #ddd", fontWeight: "bold", fontSize: "0.9rem", gap: "0.5rem", flexShrink: 0 },
  panelTextarea: { flex: 1, padding: "0.8rem", fontSize: "0.95rem", border: "none", resize: "none", fontFamily: "sans-serif", overflowY: "auto" },
  aufnahmeIndikator: { padding: "0.3rem 0.8rem", backgroundColor: "#fce8e6", color: "#c62828", fontSize: "0.85rem", flexShrink: 0 },
  mikrofonBtn: { padding: "0.3rem 0.7rem", border: "1px solid #ccc", borderRadius: "20px", cursor: "pointer", fontSize: "0.8rem" },
  kleinBtn: { padding: "0.2rem 0.5rem", backgroundColor: "#f0f2f5", border: "1px solid #ddd", borderRadius: "4px", cursor: "pointer", fontSize: "0.8rem" },
  gptBtn: { margin: "0.4rem 0.4rem 0.4rem", padding: "0.4rem 0.8rem", backgroundColor: "#7c3aed", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "0.82rem", width: "calc(100% - 0.8rem)" },
  pasteZone: { padding: "0.4rem 0.8rem", backgroundColor: "#e8f0fe", borderBottom: "1px solid #c5d5f5", fontSize: "0.82rem", color: "#1a3558", cursor: "pointer", flexShrink: 0, outline: "none", userSelect: "none" },
  screenshotListe: { flex: 1, overflowY: "auto", padding: "0.5rem" },
  screenshotItem: { marginBottom: "0.8rem", backgroundColor: "#f8f9fa", borderRadius: "4px", overflow: "hidden" },
  screenshotBild: { width: "100%", maxHeight: "180px", objectFit: "contain", cursor: "pointer", backgroundColor: "#eee" },
  screenshotFooter: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0.3rem 0.5rem" },
  screenshotName: { fontSize: "0.8rem", color: "#666", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
};
